<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import OxygenChart from '@/components/charts/OxygenChart.vue'
import TemperatureOxygenChart from '@/components/charts/TemperatureOxygenChart.vue'
import { useDeviceStore } from '@/views/Devices/store'
import { useMeasurementsStore } from '@/views/DeviceSamples/store'
import type { Device } from '@/views/Devices/models/device'
import type { DeviceSample } from '@/views/DeviceSamples/models/deviceSample'
import { useDeviceLoader } from '@/composables/useDeviceLoader'
import { useDeviceSensorManager } from '@/composables/useDeviceSensorManager'
import {
    generateGrowthReport,
    generateSensorReport,
    generateBatteryReport,
    generateComparativeReport,
    generateExecutiveReport
} from '@/config/reportGenerator'

// Stores
const deviceStore = useDeviceStore()
const measurementsStore = useMeasurementsStore()
const deviceLoader = useDeviceLoader()
const sensorManager = useDeviceSensorManager()

// Estados reactivos
const devices = ref<Device[]>([])
const allMeasurements = ref<DeviceSample[]>([])
const loading = ref(false)
const generatingReport = ref<string | null>(null)
const error = ref<string | null>(null)
const sensorDataForCharts = ref<Array<{ timestamp: string, humidity: number, oxygen: number, temperature: number, batteryLevel: number }>>([])

// Reportes disponibles para generar con datos reales
const availableReports = [
    {
        id: 'growth',
        name: 'Reporte de Crecimiento',
        description: 'Análisis completo del crecimiento de raíces con datos de todas las mediciones analizadas',
        type: 'Crecimiento',
        icon: 'mdi-chart-line',
        color: '#4CAF50',
        generate: () => generateGrowthReport(devices.value, allMeasurements.value)
    },
    {
        id: 'sensors',
        name: 'Informe de Sensores',
        description: 'Estado de los dispositivos, cantidad de muestras y estado de conexión',
        type: 'Sensores',
        icon: 'mdi-thermometer',
        color: '#2196F3',
        generate: () => generateSensorReport(devices.value, allMeasurements.value)
    },
    {
        id: 'battery',
        name: 'Reporte de Batería',
        description: 'Niveles de batería, señal y mantención de todos los dispositivos',
        type: 'Batería',
        icon: 'mdi-battery',
        color: '#FF9800',
        generate: () => generateBatteryReport(devices.value)
    },
    {
        id: 'comparative',
        name: 'Análisis Comparativo',
        description: 'Comparación de rendimiento entre dispositivos: crecimiento, salud y raíces',
        type: 'Comparativo',
        icon: 'mdi-compare',
        color: '#9C27B0',
        generate: () => generateComparativeReport(devices.value, allMeasurements.value)
    },
    {
        id: 'executive',
        name: 'Dashboard Ejecutivo',
        description: 'Resumen ejecutivo con KPIs clave, tasas de actividad y top dispositivos',
        type: 'Ejecutivo',
        icon: 'mdi-account-tie',
        color: '#795548',
        generate: () => generateExecutiveReport(devices.value, allMeasurements.value)
    }
]

const selectedReport = ref(availableReports[0])

const selectReport = (report: typeof availableReports[0]) => {
    selectedReport.value = report
}

const handleGenerate = async (report: typeof availableReports[0]) => {
    generatingReport.value = report.id
    try {
        // Pequeño delay para que se vea el loading
        await new Promise(r => setTimeout(r, 300))
        report.generate()
    } finally {
        generatingReport.value = null
    }
}

// Computed: métricas reales
const deviceMetrics = computed(() => {
    const managedMeasurements = allMeasurements.value.filter(m => m.managed)
    return {
        totalDevices: devices.value.length,
        activeDevices: devices.value.filter(d => d.active).length,
        totalMeasurements: allMeasurements.value.length,
        analyzedMeasurements: managedMeasurements.length,
        averageOxygen: 0,
        averageBattery: 0,
        totalNewRoots: managedMeasurements.reduce((s, m) => s + m.newRootsCount, 0),
        averageGrowthPercentage: managedMeasurements.length > 0
            ? Math.round(managedMeasurements.reduce((s, m) => s + m.growthPercent, 0) / managedMeasurements.length * 10) / 10
            : 0
    }
})

// Computed: datos de sensores
const sensorData = computed(() => {
    if (sensorDataForCharts.value.length > 0) return sensorDataForCharts.value
    return []
})

// Computed: últimas mediciones analizadas
const growthMeasurements = computed(() => {
    return allMeasurements.value
        .filter(m => m.managed && m.growthPercent != null)
        .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
        .slice(0, 5)
        .map(m => {
            const device = devices.value.find(d => d.id === m.deviceId)
            return {
                deviceName: device?.name || m.deviceId.slice(0, 12),
                growthPercentage: m.growthPercent,
                newRoots: m.newRootsCount,
                healthScore: m.rootHealthScore,
                date: new Date(m.time).toLocaleDateString('es-CL')
            }
        })
})

// Load real data
const loadRealData = async () => {
    try {
        loading.value = true
        await deviceLoader.loadDevicesByRole()
        devices.value = deviceStore.devices

        await measurementsStore.getMeasurements()
        allMeasurements.value = measurementsStore.deviceSamples

        if (devices.value.length > 0) {
            try {
                const firstDeviceId = devices.value[0].id
                const data = await sensorManager.loadDeviceSensorData(firstDeviceId)
                if (data.temperatureOxygenData && data.temperatureOxygenData.length > 0) {
                    sensorDataForCharts.value = data.temperatureOxygenData.map(d => ({
                        timestamp: new Date(d.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
                        humidity: 0,
                        oxygen: d.oxygen || 0,
                        temperature: d.temperature || 0,
                        batteryLevel: 0
                    }))
                }
            } catch {
                // Continuar sin datos de sensores
            }
        }
    } catch (err) {
        error.value = err instanceof Error ? err.message : 'Error cargando datos'
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    loadRealData()
})
</script>

<template>
    <v-container fluid>
        <!-- Header -->
        <v-row>
            <v-col cols="12">
                <div class="d-flex justify-space-between align-center mb-6">
                    <div>
                        <h1 class="text-h4 font-weight-bold">Reportes del Fotorizotron</h1>
                        <p class="text-body-1 text-medium-emphasis">
                            Genera reportes PDF con los datos reales de tus dispositivos y mediciones
                        </p>
                    </div>
                    <v-chip v-if="loading" color="primary" variant="tonal">
                        <v-progress-circular indeterminate size="16" width="2" class="mr-2"></v-progress-circular>
                        Cargando datos...
                    </v-chip>
                    <v-chip v-else color="success" variant="tonal">
                        {{ deviceMetrics.totalDevices }} dispositivos &bull; {{ deviceMetrics.totalMeasurements }} mediciones
                    </v-chip>
                </div>
            </v-col>
        </v-row>

        <!-- Main Content -->
        <v-row>
            <!-- Reports List -->
            <v-col cols="12" md="5">
                <v-card flat class="rounded-lg">
                    <v-card-title class="d-flex justify-space-between align-center px-4">
                        <div class="text-h6 font-weight-bold">Generar Reportes</div>
                        <v-chip color="primary" size="small">{{ availableReports.length }} tipos</v-chip>
                    </v-card-title>

                    <v-card-text>
                        <v-list>
                            <v-list-item v-for="report in availableReports" :key="report.id"
                                :class="{ 'selected-report': selectedReport.id === report.id }"
                                @click="selectReport(report)" class="mb-2 rounded-lg">
                                <template v-slot:prepend>
                                    <v-avatar :color="report.color + '20'" size="48" class="mr-3">
                                        <v-icon :color="report.color" size="24">{{ report.icon }}</v-icon>
                                    </v-avatar>
                                </template>

                                <v-list-item-title class="font-weight-medium">{{ report.name }}</v-list-item-title>
                                <v-list-item-subtitle class="mt-1 text-wrap">{{ report.description }}</v-list-item-subtitle>

                                <div class="d-flex align-center mt-2">
                                    <v-chip size="x-small" :color="report.color" variant="outlined" class="mr-2">
                                        {{ report.type }}
                                    </v-chip>
                                    <span class="text-caption text-medium-emphasis">PDF</span>
                                </div>

                                <template v-slot:append>
                                    <v-btn icon size="small" :color="report.color"
                                        :loading="generatingReport === report.id"
                                        :disabled="loading || generatingReport !== null"
                                        @click.stop="handleGenerate(report)">
                                        <v-icon>mdi-download</v-icon>
                                        <v-tooltip activator="parent" location="top">Generar y descargar PDF</v-tooltip>
                                    </v-btn>
                                </template>
                            </v-list-item>
                        </v-list>
                    </v-card-text>
                </v-card>

                <!-- Métricas del Sistema -->
                <v-card flat class="rounded-lg mt-6">
                    <v-card-title class="d-flex justify-space-between align-center px-4">
                        <div class="text-h6 font-weight-bold">Métricas del Sistema</div>
                        <v-btn variant="text" color="primary" size="small" @click="loadRealData" :loading="loading">
                            Actualizar
                        </v-btn>
                    </v-card-title>

                    <v-card-text>
                        <v-row>
                            <v-col cols="6" md="3">
                                <div class="text-center">
                                    <div class="text-h4 font-weight-bold text-primary">{{ deviceMetrics.totalDevices }}</div>
                                    <div class="text-caption text-medium-emphasis">Dispositivos</div>
                                </div>
                            </v-col>
                            <v-col cols="6" md="3">
                                <div class="text-center">
                                    <div class="text-h4 font-weight-bold text-success">{{ deviceMetrics.activeDevices }}</div>
                                    <div class="text-caption text-medium-emphasis">Activos</div>
                                </div>
                            </v-col>
                            <v-col cols="6" md="3">
                                <div class="text-center">
                                    <div class="text-h4 font-weight-bold text-info">{{ deviceMetrics.analyzedMeasurements }}</div>
                                    <div class="text-caption text-medium-emphasis">Analizadas</div>
                                </div>
                            </v-col>
                            <v-col cols="6" md="3">
                                <div class="text-center">
                                    <div class="text-h4 font-weight-bold text-purple">{{ deviceMetrics.totalNewRoots }}</div>
                                    <div class="text-caption text-medium-emphasis">Raíces Nuevas</div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Vista Previa -->
            <v-col cols="12" md="7">
                <v-card flat class="rounded-lg">
                    <v-card-title class="d-flex justify-space-between align-center px-4">
                        <div class="text-h6 font-weight-bold">Vista Previa de Datos</div>
                        <v-btn prepend-icon="mdi-download" color="primary" size="small"
                            :loading="generatingReport === selectedReport.id"
                            :disabled="loading || generatingReport !== null"
                            @click="handleGenerate(selectedReport)">
                            Generar {{ selectedReport.name }}
                        </v-btn>
                    </v-card-title>

                    <v-card-text>
                        <div class="report-preview pa-4">
                            <!-- Header del reporte seleccionado -->
                            <div class="d-flex align-center mb-6">
                                <v-avatar :color="selectedReport.color + '20'" size="48" class="mr-3">
                                    <v-icon :color="selectedReport.color" size="24">{{ selectedReport.icon }}</v-icon>
                                </v-avatar>
                                <div>
                                    <div class="text-h6 font-weight-bold">{{ selectedReport.name }}</div>
                                    <div class="text-body-2 text-medium-emphasis">
                                        {{ selectedReport.description }}
                                    </div>
                                </div>
                            </div>

                            <v-divider class="mb-6"></v-divider>

                            <!-- Loading -->
                            <div v-if="loading" class="d-flex justify-center align-center pa-12">
                                <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
                            </div>

                            <!-- Sin datos -->
                            <div v-else-if="allMeasurements.length === 0 && devices.length === 0" class="text-center pa-12">
                                <v-icon size="64" color="grey-lighten-2">mdi-database-off</v-icon>
                                <div class="text-h6 mt-3 text-medium-emphasis">Sin datos disponibles</div>
                                <div class="text-body-2 text-grey">Los reportes se generarán cuando haya dispositivos y mediciones</div>
                            </div>

                            <!-- Contenido real -->
                            <div v-else class="report-content">
                                <!-- Charts de sensores -->
                                <div class="text-h6 mb-4">Datos Actuales de Sensores</div>

                                <v-row class="mb-6">
                                    <v-col cols="12" md="6">
                                        <v-card variant="outlined" class="pa-3">
                                            <div class="text-body-1 font-weight-medium mb-2">Oxígeno Promedio</div>
                                            <div class="text-h5 font-weight-bold mb-3">{{ deviceMetrics.averageOxygen || '-' }}%</div>
                                            <OxygenChart :oxygen="deviceMetrics.averageOxygen || 18" />
                                        </v-card>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-card variant="outlined" class="pa-3">
                                            <div class="text-body-1 font-weight-medium mb-2">Crecimiento Promedio</div>
                                            <div class="text-h5 font-weight-bold text-primary mb-3">
                                                {{ deviceMetrics.averageGrowthPercentage }}%
                                            </div>
                                            <div class="text-body-2 text-medium-emphasis">
                                                Basado en {{ deviceMetrics.analyzedMeasurements }} mediciones analizadas
                                            </div>
                                        </v-card>
                                    </v-col>
                                </v-row>

                                <!-- Chart Temp-Oxígeno -->
                                <div v-if="sensorData.length > 0" class="mb-6">
                                    <div class="text-body-1 font-weight-medium mb-3">Tendencias de Temperatura y Oxígeno</div>
                                    <v-card variant="outlined" class="pa-3">
                                        <TemperatureOxygenChart :sensor-data="sensorData" title="Temperatura vs Oxígeno" height="300px" />
                                    </v-card>
                                </div>

                                <!-- Tabla de mediciones -->
                                <div class="text-h6 mt-6 mb-4">Mediciones de Crecimiento Recientes</div>

                                <v-table v-if="growthMeasurements.length > 0" density="compact">
                                    <thead>
                                        <tr>
                                            <th>Dispositivo</th>
                                            <th>Crecimiento</th>
                                            <th>Salud</th>
                                            <th>Raíces</th>
                                            <th>Fecha</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(m, i) in growthMeasurements" :key="i">
                                            <td>{{ m.deviceName }}</td>
                                            <td>
                                                <v-chip size="small"
                                                    :color="m.growthPercentage > 3 ? 'success' : m.growthPercentage > 1 ? 'warning' : 'default'">
                                                    {{ m.growthPercentage }}%
                                                </v-chip>
                                            </td>
                                            <td>{{ m.healthScore }}/10</td>
                                            <td>{{ m.newRoots }}</td>
                                            <td>{{ m.date }}</td>
                                        </tr>
                                    </tbody>
                                </v-table>
                                <div v-else class="text-center pa-6">
                                    <v-icon size="32" color="grey-lighten-2">mdi-chart-line</v-icon>
                                    <div class="text-body-2 text-medium-emphasis mt-2">Sin mediciones analizadas aún</div>
                                </div>

                                <!-- KPIs -->
                                <div class="mt-6">
                                    <v-row>
                                        <v-col cols="6" md="3">
                                            <v-card variant="outlined" class="pa-3 text-center">
                                                <div class="text-body-2 text-medium-emphasis">Tasa Análisis</div>
                                                <div class="text-h5 font-weight-bold text-primary">
                                                    {{ deviceMetrics.totalMeasurements > 0
                                                        ? Math.round(deviceMetrics.analyzedMeasurements / deviceMetrics.totalMeasurements * 100)
                                                        : 0 }}%
                                                </div>
                                            </v-card>
                                        </v-col>
                                        <v-col cols="6" md="3">
                                            <v-card variant="outlined" class="pa-3 text-center">
                                                <div class="text-body-2 text-medium-emphasis">Crec. Promedio</div>
                                                <div class="text-h5 font-weight-bold text-success">
                                                    {{ deviceMetrics.averageGrowthPercentage }}%
                                                </div>
                                            </v-card>
                                        </v-col>
                                        <v-col cols="6" md="3">
                                            <v-card variant="outlined" class="pa-3 text-center">
                                                <div class="text-body-2 text-medium-emphasis">Total Mediciones</div>
                                                <div class="text-h5 font-weight-bold text-info">
                                                    {{ deviceMetrics.totalMeasurements }}
                                                </div>
                                            </v-card>
                                        </v-col>
                                        <v-col cols="6" md="3">
                                            <v-card variant="outlined" class="pa-3 text-center">
                                                <div class="text-body-2 text-medium-emphasis">Raíces Detectadas</div>
                                                <div class="text-h5 font-weight-bold text-purple">
                                                    {{ deviceMetrics.totalNewRoots }}
                                                </div>
                                            </v-card>
                                        </v-col>
                                    </v-row>
                                </div>

                                <div class="text-body-2 mt-6 pa-3 bg-grey-lighten-5 rounded">
                                    <v-icon size="small" class="mr-2">mdi-information</v-icon>
                                    Los reportes PDF se generan con los datos actuales del sistema. Cada reporte incluye
                                    tablas detalladas, métricas y análisis basados en las mediciones reales de los dispositivos.
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<style scoped>
.selected-report {
    background-color: #F5F5F5;
    border-left: 4px solid var(--v-primary-base);
}

.report-preview {
    background-color: #FFFFFF;
    border-radius: 8px;
    border: 1px solid #E0E0E0;
}

.report-content {
    min-height: 400px;
}
</style>
